import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  Linking,
  Image,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { formatPct } from '../../utils/formatPercentage';
import { COLORS, FONTS, API_BASE_HOST } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import TargetAchievementCard from '../../components/TargetAchievementCard';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { subProcessesApi, processesApi } from '../../services/apiService';
import { FlowchartViewer } from '../../components/Flowchart';
import { SubProcess, KPIList, Process, ProcessStep } from '../../types';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';

type RouteProps = RouteProp<MainStackParamList, 'SubProcessDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ExportIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 8L12 3L7 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 3V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ClockIcon = ({ size = 16, color = '#64748B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 6V12L16 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UserIcon = ({ size = 16, color = '#64748B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UsersIcon = ({ size = 16, color = '#64748B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const BuildingIcon = ({ size = 16, color = '#64748B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 21H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M5 21V7L13 3V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19 21V11L13 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 9V9.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 12V12.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 15V15.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 18V18.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FileIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Donut Chart Component
// ============================================================

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

/**
 * Get color based on achievement percentage
 * - >= 100%: Green (#22C55E) - ممتاز
 * - >= 80% and < 100%: Orange (#F97316) - جيد
 * - < 80%: Red (#EF4444) - يحتاج تحسين
 */
const getAchievementColor = (percentage: number): string => {
  if (percentage >= 100) return '#22C55E';  // Green - Excellent
  if (percentage >= 80) return '#F97316';   // Orange - Good
  return '#EF4444';                          // Red - Needs Improvement
};

const DonutChart = ({ percentage, size = 168, strokeWidth = 12 }: DonutChartProps) => {
  const { t } = useLanguage();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  const progressColor = getAchievementColor(percentage);

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#E2E8F0"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={{ fontSize: 32, fontFamily: FONTS.numbers, fontWeight: '500', color: '#414042' }}>
          %{formatPct(percentage)}
        </Text>
        <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: '#64748B' }}>
          {t('kpis.achievementRate')}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function SubProcessDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { id } = route.params;
  const { width } = useWindowDimensions();
  const isSmallScreen = width < 768;
  const { horizontalCardWidth } = useResponsive();
  const { navigateToHome, navigateToProcessDetail, isViewOnly } = useRoleNavigation();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  // DTS 2.2: only Strategy Team (hrcpms.soft_delete) can delete operational
  // items. Section Head has create/edit; delete is Strategy Team's only.
  const canSoftDelete = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.soft_delete')
  );
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: isDark ? '#374151' : '#F3F4F6',
    sectionBg: isDark ? '#374151' : '#F1F5F9',
  };

  const [subProcess, setSubProcess] = useState<SubProcess | null>(null);
  const [parentProcess, setParentProcess] = useState<Process | null>(null);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.position_type === 'EMPLOYEE';
  const canApproveDeletion = (user?.is_superuser || user?.position_type === 'MANAGER' || user?.position_type === 'DIRECTOR' || (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false));

  // Steps from API (subProcess.steps)
  const steps: ProcessStep[] = subProcess?.steps || [];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const subProcessData = await subProcessesApi.get(id);

      setSubProcess(subProcessData);
      // Use KPIs from main response instead of separate API call
      setKpis(subProcessData.kpis || []);

      // Fetch parent process data
      if (subProcessData.process) {
        try {
          const parentData = await processesApi.get(subProcessData.process);
          setParentProcess(parentData);
        } catch {
          // Parent process fetch failed, continue without it
        }
      }
    } catch (err) {
      showError(t('subProcesses.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, showError]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const handleEdit = () => {
    if (subProcess) {
      navigation.navigate('AddSubProcess', {
        processId: subProcess.process,
        subProcessId: subProcess.id
      });
    }
  };

  const handleDelete = () => {
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await subProcessesApi.delete(id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      } else {
        showSuccess(t('subProcesses.deleteSuccess'));
      }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        navigation.goBack();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('messages.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
    }
  };

  const handleExportPDF = () => {
    if (!subProcess) {
      showError(t('common.noDataToExport'));
      return;
    }

    // Create printable HTML content
    const stepsHTML = steps.length > 0
      ? steps.map((step, index) => `
          <tr>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${step.tools || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${step.duration || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${step.responsible || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${step.description || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right; font-weight: 600;">${step.name}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: center; font-weight: 600;">${index + 1}</td>
          </tr>
        `).join('')
      : `<tr><td colspan="6" style="padding: 20px; text-align: center; color: #6B7280;">${t('subProcesses.noSteps')}</td></tr>`;

    const kpisHTML = kpis.length > 0
      ? kpis.map(kpi => `
          <tr>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: center;">${kpi.achievement_percentage || 0}%</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${kpi.target_value || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${kpi.measurement_frequency || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${kpi.name}</td>
          </tr>
        `).join('')
      : `<tr><td colspan="4" style="padding: 20px; text-align: center; color: #6B7280;">${t('kpis.noKPIs')}</td></tr>`;

    const htmlContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>${t('processes.subProcessDetails')} - ${subProcess.name}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 40px;
            background: #fff;
            color: #1F2937;
            direction: rtl;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #A9211F;
          }
          .header h1 {
            color: #A9211F;
            font-size: 24px;
            margin-bottom: 8px;
          }
          .header p {
            color: #6B7280;
            font-size: 14px;
          }
          .section {
            margin-bottom: 30px;
          }
          .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #1F2937;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 1px solid #E5E7EB;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin-bottom: 20px;
          }
          .info-item {
            background: #F9FAFB;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid #E5E7EB;
          }
          .info-label {
            font-size: 12px;
            color: #6B7280;
            margin-bottom: 4px;
          }
          .info-value {
            font-size: 16px;
            font-weight: 600;
            color: #1F2937;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 12px;
          }
          th {
            background: #F3F4F6;
            padding: 12px;
            border: 1px solid #E5E7EB;
            text-align: right;
            font-weight: 600;
            color: #374151;
          }
          .description {
            background: #F9FAFB;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid #E5E7EB;
            line-height: 1.8;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #E5E7EB;
            text-align: center;
            color: #9CA3AF;
            font-size: 12px;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${t('processes.subProcessDetails')}</h1>
          <p>${subProcess.name}</p>
        </div>

        <div class="section">
          <h2 class="section-title">${t('common.basicInfo')}</h2>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">${t('processes.subProcessName')}</div>
              <div class="info-value">${subProcess.name}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('subProcesses.mainProcess')}</div>
              <div class="info-value">${parentProcess?.name || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('unitGoals.organizationalUnit')}</div>
              <div class="info-value">${subProcess.organizational_unit_name || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('processes.processTime')}</div>
              <div class="info-value">${subProcess.duration_display || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('processes.employeesCount')}</div>
              <div class="info-value">${subProcess.employees_count || 0}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('processes.executionFrequency')}</div>
              <div class="info-value">${subProcess.execution_frequency || '-'}</div>
            </div>
          </div>
        </div>

        ${subProcess.description ? `
        <div class="section">
          <h2 class="section-title">${t('common.description')}</h2>
          <div class="description">${subProcess.description}</div>
        </div>
        ` : ''}

        <div class="section">
          <h2 class="section-title">${t('subProcesses.processSteps')} (${steps.length})</h2>
          <table>
            <thead>
              <tr>
                <th>${t('processes.tools')}</th>
                <th>${t('processes.duration')}</th>
                <th>${t('processes.responsible')}</th>
                <th>${t('common.description')}</th>
                <th>${t('processes.stepName')}</th>
                <th style="width: 50px;">${t('processes.stepOrder')}</th>
              </tr>
            </thead>
            <tbody>
              ${stepsHTML}
            </tbody>
          </table>
        </div>

        <div class="section">
          <h2 class="section-title">${t('kpis.performanceIndicators')} (${kpis.length})</h2>
          <table>
            <thead>
              <tr>
                <th style="width: 100px;">${t('kpis.achievementPercentage')}</th>
                <th>${t('kpis.targetValue')}</th>
                <th>${t('kpis.measurementFrequency')}</th>
                <th>${t('kpis.kpiName')}</th>
              </tr>
            </thead>
            <tbody>
              ${kpisHTML}
            </tbody>
          </table>
        </div>

        <div class="footer">
          <p>${t('common.exportedOn')}: ${new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p>${t('common.appName')}</p>
        </div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();

      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } else {
      showError(t('common.printWindowError'));
    }
  };

  const handleAddKPI = () => {
    if (subProcess) {
      // Navigate to SubProcess Edit page with scrollToKpi flag
      navigation.navigate('AddSubProcess', {
        processId: subProcess.process,
        subProcessId: subProcess.id,
        scrollToKpi: true
      });
    }
  };

  const handleAddOperation = () => {
    if (subProcess) {
      navigation.navigate('AddOperation', {
        processId: subProcess.process,
        subProcessId: subProcess.id,
      });
    }
  };

  const handleViewKPIDetails = (kpiId: number) => {
    navigation.navigate('KPIDetail', { id: kpiId });
  };

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoToParentProcess = () => {
    if (subProcess?.process) {
      navigation.navigate('ProcessDetail', { id: subProcess.process });
    }
  };

  // Calculate achievement percentage from KPIs (use backend cumulative value)
  // null when no KPIs — shows "--" instead of 0%
  const achievementPercentage = kpis.length > 0
    ? kpis.reduce((acc, kpi) => acc + parseFloat(kpi.achievement_percentage || '0'), 0) / kpis.length
    : null;

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={[styles.loadingText, { color: colors.textMuted }]}>{t('common.loading')}</Text>
        </View>
      </PageContainer>
    );
  }

  if (!subProcess) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.errorContainer}>
          <Text style={[styles.errorText, { color: colors.textMuted }]}>{t('subProcesses.notFound')}</Text>
          <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
            <Text style={styles.backButtonText}>{t('common.back')}</Text>
          </TouchableOpacity>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.content}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t("navigation.home") },
              { label: t("navigation.processes"), route: "Processes" },
              {
                label: t("processes.processDetails"),
                route: "ProcessDetail",
                params: { id: subProcess?.process },
              },
              { label: t("subProcesses.title") },
            ]}
          />

          {/* Header Section */}
          <View style={styles.headerSection}>
            <View
              style={[
                styles.headerContainer,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
              ]}
            >
              <View style={styles.headerTitleRow}>
                <View style={styles.headerActions}>
                  <TouchableOpacity
                    style={[
                      styles.exportButton,
                      {
                        backgroundColor: themeColors.sectionBg,
                        borderColor: themeColors.border,
                      },
                    ]}
                    onPress={handleExportPDF}
                  >
                    <Text
                      style={[
                        styles.exportButtonText,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {t("common.exportPDF")}
                    </Text>
                    <ExportIcon size={13} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                  {canSoftDelete && (
                    <TouchableOpacity
                      style={[
                        styles.editButton,
                        {
                          backgroundColor: '#FEE2E2',
                          borderColor: '#FECACA',
                        },
                      ]}
                      onPress={handleDelete}
                    >
                      <Text style={{ fontSize: 13, color: '#DC2626', fontFamily: FONTS.medium }}>
                        {t("common.delete")}
                      </Text>
                    </TouchableOpacity>
                  )}
                  {/* Edit عملية فرعية is exclusive to Employee + Section Head.
                      Strategy Section Head (strategic_goals.create → isViewOnly),
                      Director, and DG do NOT propose/edit operational items.
                      Same isViewOnly gate the Add button + AddSubProcess screen use. */}
                  {!isViewOnly && (
                    <TouchableOpacity
                      style={[
                        styles.editButton,
                        {
                          backgroundColor: themeColors.sectionBg,
                          borderColor: themeColors.border,
                        },
                      ]}
                      onPress={handleEdit}
                    >
                      <Text
                        style={[
                          styles.editButtonText,
                          { color: themeColors.textPrimary },
                        ]}
                      >
                        {t("common.edit")}
                      </Text>
                      <EditIcon size={13} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                  )}
                </View>
                <View style={styles.headerTitleWrapper}>
                  <Text
                    style={[
                      styles.headerTitle,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {translate(subProcess.name)}
                  </Text>
                  <Text
                    style={[
                      styles.headerDescription,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {translate(subProcess.description) ||
                      t("common.noDescription")}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Pending Deletion Banner */}
          {(subProcess as any).pending_deletion_info && (
            <DeletionApprovalBanner
              deletionInfo={(subProcess as any).pending_deletion_info}
              canApprove={canApproveDeletion}
              canCancel={isOrgEmployee && (subProcess as any).pending_deletion_info.requested_by_id === user?.id}
              onApproved={() => { showSuccess(t('deletion.approved')); navigation.goBack(); }}
              onRejected={() => { showSuccess(t('deletion.rejected')); fetchData(); }}
              onCancelled={() => { showSuccess(t('deletion.requestCancelled')); fetchData(); }}
              onViewImpact={() => setShowDeletionImpact(true)}
            />
          )}

          {/* Main Content - Donut Chart and Basic Info */}
          <View
            style={[
              styles.mainContentRow,
              isSmallScreen && styles.mainContentRowSmall,
            ]}
          >
            {/* Right - Basic Information */}
            <View
              style={[
                styles.basicInfoSection,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
                isSmallScreen && styles.basicInfoSectionSmall,
              ]}
            >
              <Text
                style={[
                  styles.basicInfoTitle,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("common.basicInfo")}
              </Text>

              <View style={styles.infoGrid}>
                <View style={styles.infoRow}>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("subProcesses.mainProcess")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {translate(parentProcess?.name) || "-"}
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("unitGoals.organizationalUnit")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {translate(subProcess.organizational_unit_name) || "-"}
                    </Text>
                  </View>
                </View>

                <View style={styles.infoRow}>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("common.classification")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {t("subProcesses.sub")}
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.infoItem,
                      { backgroundColor: themeColors.sectionBg },
                    ]}
                  >
                    <Text
                      style={[
                        styles.infoLabel,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("subProcesses.processTime")}
                    </Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: themeColors.textPrimary },
                      ]}
                    >
                      {subProcess.duration_display || "-"}
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            {/* Left - Donut Chart */}
            <View
              style={[
                styles.donutSection,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
                isSmallScreen && styles.donutSectionSmall,
              ]}
            >
              <Text
                style={[
                  styles.sectionLabel,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("kpis.performanceIndicators")}
              </Text>
              {achievementPercentage != null ? (
                <DonutChart
                  percentage={achievementPercentage}
                  size={168}
                  strokeWidth={14}
                />
              ) : (
                <View style={{ width: 168, height: 168, justifyContent: 'center', alignItems: 'center' }}>
                  <Text style={{ fontSize: 36, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
              {/* Legend Items */}
              <View style={styles.donutLegendItems}>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: "#22C55E" },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.excellent")} (≥100%)
                  </Text>
                </View>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: "#F97316" },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.good")} (≥80%)
                  </Text>
                </View>
                <View style={styles.donutLegendRow}>
                  <View
                    style={[
                      styles.donutLegendDot,
                      { backgroundColor: "#EF4444" },
                    ]}
                  />
                  <Text
                    style={[
                      styles.donutLegendLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("kpis.needsImprovement")}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Flowchart (read-only). Renders next to the legacy
              process_flow_diagram file attachment, if any. */}
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 16, fontWeight: 'bold', textAlign: 'right', color: themeColors.textPrimary, marginBottom: 8 }}>
              {t('flowchart.sectionTitle')}
            </Text>
            <FlowchartViewer
              value={(subProcess as any)?.flowchart_data || null}
              height={360}
              emptyMessage={t('flowchart.emptyMessage')}
            />
          </View>

          {/* KPIs Section */}
          <View
            style={[
              styles.kpisSection,
              {
                backgroundColor: themeColors.card,
                borderColor: themeColors.cardBorder,
              },
            ]}
          >
            <View style={styles.kpisSectionHeader}>
              {/* Add KPI routes into the sub-process edit form — same
                  Employee + Section Head exclusivity as the Edit button. */}
              {!isViewOnly && (
                <TouchableOpacity
                  style={[styles.addKpiBtn, { borderColor: themeColors.border }]}
                  onPress={handleAddKPI}
                >
                  <Text
                    style={[
                      styles.addKpiBtnText,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {t("kpis.addKPI")}
                  </Text>
                  <PlusIcon size={13} color={themeColors.iconDefault} />
                </TouchableOpacity>
              )}
              <Text
                style={[
                  styles.kpisSectionTitle,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("kpis.performanceIndicators")}
              </Text>
            </View>

            <View style={styles.rtlWrapper}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={true}
                contentContainerStyle={styles.horizontalCardsRow}
                style={styles.horizontalScrollContainer}
              >
                {kpis.length > 0 ? (
                  kpis.map((kpi) => {
                    const achievement = parseFloat(kpi.achievement_percentage || '0');
                    const progressColor =
                      achievement >= 80
                        ? "#2CA02C"
                        : achievement >= 50
                          ? "#EAB308"
                          : "#EF4444";

                    return (
                      <View
                        key={kpi.id}
                        style={[styles.rtlCard, { width: horizontalCardWidth }]}
                      >
                        <TargetAchievementCard
                          title={translate(kpi.name)}
                          achieved={kpi.latest_value || '0'}
                          target={kpi.target_value}
                          unit={kpi.unit === 'percentage' ? '%' : translate(kpi.unit === 'count' ? 'عدد' : kpi.unit)}
                          achievedColor={progressColor}
                          onViewDetails={() => handleViewKPIDetails(kpi.id)}
                        />
                      </View>
                    );
                  })
                ) : (
                  <View style={[styles.emptyStateContainer, styles.rtlCard]}>
                    <Text
                      style={[
                        styles.emptyStateText,
                        { color: themeColors.textMuted },
                      ]}
                    >
                      {t("kpis.noKPIs")}
                    </Text>
                    {!isViewOnly && (
                      <TouchableOpacity
                        style={[
                          styles.addKpiEmptyBtn,
                          { borderColor: themeColors.border },
                        ]}
                        onPress={handleAddKPI}
                      >
                        <Text
                          style={[
                            styles.addKpiEmptyBtnText,
                            { color: themeColors.textPrimary },
                          ]}
                        >
                          {t("kpis.addKPI")}
                        </Text>
                        <PlusIcon size={13} color={themeColors.iconDefault} />
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </ScrollView>
            </View>
          </View>

          {/* Resources and Execution Section - الموارد والتنفيذ */}
          <View
            style={[
              styles.resourcesSection,
              {
                backgroundColor: themeColors.card,
                borderColor: themeColors.cardBorder,
              },
            ]}
          >
            <Text
              style={[
                styles.resourcesSectionTitle,
                { color: themeColors.textPrimary },
              ]}
            >
              {t("subProcesses.resourcesAndExecution")}
            </Text>
            <View style={styles.resourcesGrid}>
              <View
                style={[
                  styles.resourcesRow,
                  isSmallScreen && styles.resourcesRowSmall,
                ]}
              >
                <View
                  style={[
                    styles.resourceItem,
                    { backgroundColor: themeColors.sectionBg },
                  ]}
                >
                  <Text
                    style={[
                      styles.resourceLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("processes.executionFrequency")}
                  </Text>
                  <Text
                    style={[
                      styles.resourceValue,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {subProcess.execution_frequency
                      ? { daily: t('kpis.daily'), weekly: t('kpis.weekly'), monthly: t('kpis.monthly'), quarterly: t('kpis.quarterly'), half_yearly: t('kpis.semiAnnual'), yearly: t('kpis.annual') }[subProcess.execution_frequency] || subProcess.execution_frequency
                      : "-"}
                  </Text>
                </View>
                <View
                  style={[
                    styles.resourceItem,
                    { backgroundColor: themeColors.sectionBg },
                  ]}
                >
                  <Text
                    style={[
                      styles.resourceLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("subProcesses.executionEmployeesCount")}
                  </Text>
                  <Text
                    style={[
                      styles.resourceValue,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {subProcess.employees_count || "-"}
                  </Text>
                </View>
              </View>

              <View
                style={[
                  styles.resourcesRow,
                  isSmallScreen && styles.resourcesRowSmall,
                ]}
              >
                <View
                  style={[
                    styles.resourceItem,
                    { backgroundColor: themeColors.sectionBg },
                  ]}
                >
                  <Text
                    style={[
                      styles.resourceLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("subProcesses.processCost")}
                  </Text>
                  <Text
                    style={[
                      styles.resourceValue,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {subProcess.cost ? `${subProcess.cost}` : "-"}
                  </Text>
                </View>
                <View
                  style={[
                    styles.resourceItem,
                    { backgroundColor: themeColors.sectionBg },
                  ]}
                >
                  <Text
                    style={[
                      styles.resourceLabel,
                      { color: themeColors.textMuted },
                    ]}
                  >
                    {t("subProcesses.approvalPerson")}
                  </Text>
                  <Text
                    style={[
                      styles.resourceValue,
                      { color: themeColors.textPrimary },
                    ]}
                  >
                    {subProcess.approval_person || "-"}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Process Steps Section - خطوات العملية */}
          <View
            style={[
              styles.stepsSection,
              {
                backgroundColor: themeColors.card,
                borderColor: themeColors.cardBorder,
              },
            ]}
          >
            <View style={{ flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text
                style={[
                  styles.stepsSectionTitle,
                  { color: themeColors.textPrimary, marginBottom: 0 },
                ]}
              >
                {t("subProcesses.processSteps")}
              </Text>
              {!isViewOnly && (
                <TouchableOpacity
                  style={[styles.editButton, { backgroundColor: themeColors.sectionBg, borderColor: themeColors.border }]}
                  onPress={handleAddOperation}
                >
                  <Text style={[styles.editButtonText, { color: themeColors.textPrimary }]}>{t('operations.addOperation')}</Text>
                </TouchableOpacity>
              )}
            </View>

            {steps.length > 0 ? (
              steps.map((step) => (
                <View
                  key={step.id}
                  style={[
                    styles.stepCard,
                    {
                      backgroundColor: themeColors.sectionBg,
                      borderColor: themeColors.cardBorder,
                    },
                  ]}
                >
                  <View style={styles.stepCardContent}>
                    {/* Step Header */}
                    <View style={styles.stepHeader}>
                      <Text
                        style={[
                          styles.stepTitle,
                          { color: themeColors.textPrimary },
                        ]}
                      >
                        {translate(step.name)}
                      </Text>
                      <View style={styles.stepMeta}>
                        {step.duration && (
                          <View style={styles.stepMetaItem}>
                            <Text
                              style={[
                                styles.stepMetaText,
                                { color: themeColors.textMuted },
                              ]}
                            >
                              {translate(step.duration)}
                            </Text>
                            <ClockIcon
                              size={14}
                              color={themeColors.textMuted}
                            />
                          </View>
                        )}
                        {step.responsible && (
                          <View style={styles.stepMetaItem}>
                            <Text
                              style={[
                                styles.stepMetaText,
                                { color: themeColors.textMuted },
                              ]}
                            >
                              {translate(step.responsible)}
                            </Text>
                            <UserIcon size={14} color={themeColors.textMuted} />
                          </View>
                        )}
                      </View>
                    </View>

                    {/* Step Description */}
                    {step.description && (
                      <View style={styles.stepDescriptionRow}>
                        <Text
                          style={[
                            styles.stepDescriptionLabel,
                            { color: themeColors.textMuted },
                          ]}
                        >
                          {t("common.description")}:
                        </Text>
                        <Text
                          style={[
                            styles.stepDescriptionText,
                            { color: themeColors.textSecondary },
                          ]}
                        >
                          {translate(step.description)}
                        </Text>
                      </View>
                    )}

                    {/* Step Tools */}
                    {step.tools && (
                      <View style={styles.stepToolsRow}>
                        <Text
                          style={[
                            styles.stepToolsLabel,
                            { color: themeColors.textMuted },
                          ]}
                        >
                          {t("subProcesses.tools")}:
                        </Text>
                        <Text
                          style={[
                            styles.stepToolsText,
                            { color: themeColors.textSecondary },
                          ]}
                        >
                          {translate(step.tools)}
                        </Text>
                      </View>
                    )}

                    {/* Organizational Units (ManyToMany) */}
                    {step.organizational_units_details &&
                      step.organizational_units_details.length > 0 && (
                        <View style={styles.stepAssignmentRow}>
                          <View style={styles.stepAssignmentHeader}>
                            <BuildingIcon size={14} color="#3B82F6" />
                            <Text
                              style={[
                                styles.stepAssignmentLabel,
                                { color: themeColors.textMuted },
                              ]}
                            >
                              {t("subProcesses.responsibleUnits")}:
                            </Text>
                          </View>
                          <View style={styles.stepTagsContainer}>
                            {step.organizational_units_details.map((unit) => (
                              <View key={unit.id} style={styles.stepUnitTag}>
                                <Text style={styles.stepUnitTagText}>
                                  {translate(unit.name_ar || unit.name)}
                                </Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      )}

                    {/* Responsible Employees (ManyToMany) */}
                    {step.responsible_employees_details &&
                      step.responsible_employees_details.length > 0 && (
                        <View style={styles.stepAssignmentRow}>
                          <View style={styles.stepAssignmentHeader}>
                            <UsersIcon size={14} color="#22C55E" />
                            <Text
                              style={[
                                styles.stepAssignmentLabel,
                                { color: themeColors.textMuted },
                              ]}
                            >
                              {t("subProcesses.responsibleEmployees")}:
                            </Text>
                          </View>
                          <View style={styles.stepTagsContainer}>
                            {step.responsible_employees_details.map((emp) => (
                              <View key={emp.id} style={styles.stepEmployeeTag}>
                                <Text style={styles.stepEmployeeTagText}>
                                  {translate(emp.name_ar || emp.name_en)}
                                  {emp.position && (
                                    <Text style={styles.stepEmployeePosition}>
                                      {" "}
                                      ({translate(emp.position)})
                                    </Text>
                                  )}
                                </Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      )}
                  </View>

                  {/* Step Number */}
                  <View style={styles.stepNumberContainer}>
                    <Text style={styles.stepNumber}>{step.order}</Text>
                  </View>
                </View>
              ))
            ) : (
              <View style={styles.emptyStateContainer}>
                <Text
                  style={[
                    styles.emptyStateText,
                    { color: themeColors.textMuted },
                  ]}
                >
                  {t("subProcesses.noSteps")}
                </Text>
              </View>
            )}
          </View>

          {/* Process Flow Diagram Section - المسار التدفقي للعملية */}
          {subProcess.process_flow_diagram && (
            <View
              style={[
                styles.fileSection,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
              ]}
            >
              <Text
                style={[
                  styles.fileSectionTitle,
                  { color: themeColors.textPrimary },
                ]}
              >
                {t("subProcesses.processFlowImages")}
              </Text>

              {/* Image Display */}
              <TouchableOpacity
                onPress={() => {
                  const fileUrl = subProcess.process_flow_diagram?.startsWith(
                    "http",
                  )
                    ? subProcess.process_flow_diagram
                    : `${API_BASE_HOST}${subProcess.process_flow_diagram}`;
                  Linking.openURL(fileUrl);
                }}
                activeOpacity={0.8}
              >
                <Image
                  source={{
                    uri: subProcess.process_flow_diagram?.startsWith("http")
                      ? subProcess.process_flow_diagram
                      : `${API_BASE_HOST}${subProcess.process_flow_diagram}`,
                  }}
                  style={styles.processFlowImage}
                  resizeMode="contain"
                />
              </TouchableOpacity>

              {/* Download/View Button */}
              <TouchableOpacity
                style={[
                  styles.fileDownloadButton,
                  {
                    backgroundColor: themeColors.sectionBg,
                    borderColor: themeColors.cardBorder,
                  },
                ]}
                onPress={() => {
                  const fileUrl = subProcess.process_flow_diagram?.startsWith(
                    "http",
                  )
                    ? subProcess.process_flow_diagram
                    : `${API_BASE_HOST}${subProcess.process_flow_diagram}`;
                  Linking.openURL(fileUrl);
                }}
              >
                <FileIcon size={20} color={themeColors.brand} />
                <Text
                  style={[
                    styles.fileDownloadText,
                    { color: themeColors.textPrimary },
                  ]}
                >
                  {t("common.viewFullSize")}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDelete}
        entityName={subProcess?.name || ''}
        fetchImpact={async () => {
          const response = await subProcessesApi.deletionImpact(id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FAFAFA',
  },
  content: {
    padding: 24,
    paddingTop: 16,
    gap: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  errorText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#EF4444',
  },
  backButton: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  backButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breadcrumbText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

  // Header Section
  headerSection: {
    marginBottom: 0,
  },
  headerContainer: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  headerTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerTitleWrapper: {
    flex: 1,
    alignItems: 'flex-end',
    gap: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },
  headerDescription: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    gap: 6,
    minHeight: 32,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  editButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    gap: 6,
    minHeight: 32,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  exportButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },

  // Main Content Row
  mainContentRow: {
    flexDirection: 'row-reverse',
    gap: 24,
  },
  mainContentRowSmall: {
    flexDirection: 'column',
  },

  // Basic Info Section
  basicInfoSection: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 24,
  },
  basicInfoSectionSmall: {
    width: '100%',
  },
  basicInfoTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 20,
  },
  infoGrid: {
    gap: 16,
  },
  infoRow: {
    flexDirection: 'row-reverse',
    gap: 24,
  },
  infoItem: {
    flex: 1,
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    padding: 16,
  },
  infoLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    marginBottom: 8,
  },
  infoValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
    textAlign: 'right',
  },

  // Donut Section
  donutSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 24,
    alignItems: 'center',
    width: 240,
  },
  donutSectionSmall: {
    width: '100%',
  },
  sectionLabel: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    marginBottom: 20,
  },
  donutLegendItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
    marginTop: 16,
  },
  donutLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  donutLegendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  donutLegendLabel: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },

  // Resources Section - الموارد والتنفيذ
  resourcesSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 24,
  },
  resourcesSectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 20,
  },
  resourcesGrid: {
    gap: 16,
  },
  resourcesRow: {
    flexDirection: 'row-reverse',
    gap: 24,
  },
  resourcesRowSmall: {
    flexDirection: 'column',
    gap: 16,
  },
  resourceItem: {
    flex: 1,
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    padding: 16,
  },
  resourceLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    marginBottom: 8,
  },
  resourceValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
    textAlign: 'right',
  },

  // File Section - المسار التدفقي للعملية
  fileSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 24,
    marginBottom: 16,
  },
  fileSectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
  },
  processFlowImage: {
    width: '100%',
    height: 400,
    borderRadius: 8,
    marginBottom: 16,
    backgroundColor: '#F9FAFB',
  },
  fileDownloadButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
  },
  fileDownloadText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'center',
  },

  // Steps Section - خطوات العملية
  stepsSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 24,
  },
  stepsSectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 20,
  },
  stepCard: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    marginBottom: 16,
    borderRightWidth: 4,
    borderRightColor: '#CBD5E1',
    overflow: 'hidden',
  },
  stepCardContent: {
    flex: 1,
    padding: 16,
    gap: 12,
  },
  stepHeader: {
    gap: 8,
  },
  stepTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },
  stepMeta: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 16,
  },
  stepMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  stepMetaText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  stepDescriptionRow: {
    flexDirection: 'row-reverse',
    gap: 8,
  },
  stepDescriptionLabel: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#64748B',
  },
  stepDescriptionText: {
    flex: 1,
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  stepToolsRow: {
    flexDirection: 'row-reverse',
    gap: 8,
  },
  stepToolsLabel: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#64748B',
  },
  stepToolsText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Step Assignment Styles (for ManyToMany display)
  stepAssignmentRow: {
    gap: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  stepAssignmentHeader: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
  },
  stepAssignmentLabel: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#64748B',
  },
  stepTagsContainer: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 8,
  },
  stepUnitTag: {
    backgroundColor: '#EFF6FF',
    borderWidth: 1,
    borderColor: '#BFDBFE',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  stepUnitTagText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#1D4ED8',
    textAlign: 'right',
  },
  stepEmployeeTag: {
    backgroundColor: '#F0FDF4',
    borderWidth: 1,
    borderColor: '#BBF7D0',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  stepEmployeeTagText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#166534',
    textAlign: 'right',
  },
  stepEmployeePosition: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  stepNumberContainer: {
    width: 48,
    backgroundColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepNumber: {
    fontSize: 24,
    fontFamily: FONTS.medium,
    color: '#414042',
  },

  // KPIs Section
  kpisSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 24,
  },
  kpisSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  kpisSectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  addKpiBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addKpiBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },

  // KPI Cards Grid
  // Horizontal scroll container for KPI cards
  horizontalScrollContainer: {
    flexGrow: 0,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 4,
    paddingBottom: 12,
  },
  kpiCardNew: {
    width: 288, // Fixed width (w-72)
    flexShrink: 0, // Prevent shrinking
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 16,
  },
  kpiCardContent: {
    gap: 16,
  },
  kpiCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    minHeight: 40,
  },
  kpiProgressRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
  },
  kpiProgressBadge: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 100,
  },
  kpiProgressBadgeText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'center',
  },
  kpiProgressBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    position: 'relative',
    overflow: 'visible',
    flexDirection: 'row-reverse',
  },
  kpiProgressBarFill: {
    height: 8,
    borderRadius: 8,
  },
  kpiProgressDot: {
    position: 'absolute',
    top: -3,
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 2,
    borderColor: '#FCFCFC',
  },
  kpiViewDetailsBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  kpiViewDetailsBtnText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },

  // Empty State
  emptyStateContainer: {
    alignItems: 'center',
    paddingVertical: 32,
    gap: 16,
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  addKpiEmptyBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  addKpiEmptyBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
});
